/**
 * Preference key constants
 * Use these constants to avoid typos and ensure consistency across the application
 *
 * Namespace structure:
 * - ui.*: UI preferences (user-scoped)
 * - view.*: View modes (user-scoped)
 * - sort.*: Sort preferences (user-scoped)
 * - filter.*: Filter defaults (user-scoped)
 * - notifications.*: Notification settings (user-scoped)
 * - workspace.*: Workspace settings (workspace-scoped)
 * - defaults.*: Form defaults (workspace-scoped)
 */

export const PREF_KEYS = {
  /**
   * UI Preferences (user-scoped)
   * Settings that control the user interface appearance and behavior
   */
  UI: {
    SIDEBAR_COLLAPSED: 'ui.sidebar.collapsed' as const,
    THEME_MODE: 'ui.theme.mode' as const,
    COLLECTIONS_FILTER_OPEN: 'ui.collections.filter.open' as const,
    DENSITY: 'ui.density' as const,
  },

  /**
   * View Mode Preferences (user-scoped)
   * How different pages display their content (grid, list, table)
   */
  VIEW: {
    MODELS_MODE: 'view.models.mode' as const,
    JOBS_MODE: 'view.jobs.mode' as const,
    PRINTERS_MODE: 'view.printers.mode' as const,
    SPOOLS_MODE: 'view.spools.mode' as const,
  },

  /**
   * Sort Preferences (user-scoped)
   * Default sorting for lists and tables
   */
  SORT: {
    MODELS_COLUMN: 'sort.models.column' as const,
    MODELS_ORDER: 'sort.models.order' as const,
    JOBS_COLUMN: 'sort.jobs.column' as const,
    JOBS_ORDER: 'sort.jobs.order' as const,
    SPOOLS_COLUMN: 'sort.spools.column' as const,
    SPOOLS_ORDER: 'sort.spools.order' as const,
  },

  /**
   * Filter Preferences (user-scoped)
   * Saved filter states
   */
  FILTER: {
    MODELS_SELECTED_COLLECTIONS: 'filter.models.selected.collections' as const,
    MODELS_FAVORITES_ONLY: 'filter.models.favorites.only' as const,
    JOBS_STATUS: 'filter.jobs.status' as const,
  },

  /**
   * Notification Preferences (user-scoped)
   * Email and in-app notification settings
   */
  NOTIFICATIONS: {
    EMAIL_LOW_FILAMENT: 'notifications.email.low.filament' as const,
    EMAIL_JOB_COMPLETE: 'notifications.email.job.complete' as const,
    EMAIL_JOB_FAILED: 'notifications.email.job.failed' as const,
    IN_APP_ENABLED: 'notifications.inapp.enabled' as const,
  },

  /**
   * Workspace Settings (workspace-scoped)
   * Settings shared by all workspace members
   */
  WORKSPACE: {
    TIMEZONE: 'workspace.timezone' as const,
    DATE_FORMAT: 'workspace.date.format' as const,
    UNITS: 'workspace.units' as const,
  },

  /**
   * Default Values (workspace-scoped)
   * Default form values for the workspace
   */
  DEFAULTS: {
    PRINTER_BRAND: 'defaults.printer.brand' as const,
    FILAMENT_MANUFACTURER: 'defaults.filament.manufacturer' as const,
    FILAMENT_TYPE: 'defaults.filament.type' as const,
  },
} as const;

/**
 * Type representing all possible preference keys
 */
export type PreferenceKey = (typeof PREF_KEYS)[keyof typeof PREF_KEYS][keyof (typeof PREF_KEYS)[keyof typeof PREF_KEYS]];
